import type { PlaybookStep } from '@chatsdet/types';
import { stepMatchesCandidate, type LiteralSignal } from './step-linker-candidates.js';
import type { CandidateAction } from './step-linker-groups.js';

interface LinkClaim {
  stepOrder: number;
  actionIndex: number;
  layer: number;
}

interface StepInterval {
  afterStep: number;
  beforeStep: number;
  afterAction: number;
  beforeAction: number;
}

export function solveStepIntervals(args: {
  steps: PlaybookStep[];
  candidatesForStep: (step: PlaybookStep) => CandidateAction[];
  discriminatingLiterals: Map<number, LiteralSignal>;
  claimsByStep: ReadonlyMap<number, LinkClaim>;
  tryClaim: (stepOrder: number, actionIndex: number, layer: 4) => boolean;
}): void {
  const anchors = [...args.claimsByStep.values()].sort((a, b) => a.stepOrder - b.stepOrder);
  const intervals: StepInterval[] = [];

  let prevStep = -Infinity;
  let prevAction = -Infinity;
  for (const anchor of anchors) {
    intervals.push({
      afterStep: prevStep,
      beforeStep: anchor.stepOrder,
      afterAction: prevAction,
      beforeAction: anchor.actionIndex,
    });
    prevStep = anchor.stepOrder;
    prevAction = anchor.actionIndex;
  }
  intervals.push({
    afterStep: prevStep,
    beforeStep: Infinity,
    afterAction: prevAction,
    beforeAction: Infinity,
  });

  for (const interval of intervals) {
    let changed = true;
    while (changed) {
      changed = false;
      const openSteps = args.steps.filter(
        (step) =>
          !args.claimsByStep.has(step.order) &&
          step.order > interval.afterStep &&
          step.order < interval.beforeStep
      );
      if (openSteps.length === 0) break;

      const stepCandidates = new Map<number, CandidateAction[]>();
      for (const step of openSteps) {
        const dynamicInterval = actionIntervalForSteps(step.order, step.order, args.claimsByStep);
        const candidates = args
          .candidatesForStep(step)
          .filter(
            (candidate) =>
              !isActionClaimed(args.claimsByStep, candidate.index) &&
              candidate.index > interval.afterAction &&
              candidate.index < interval.beforeAction &&
              candidate.index > dynamicInterval.afterAction &&
              candidate.index < dynamicInterval.beforeAction &&
              stepMatchesCandidate(step, candidate, args.discriminatingLiterals.get(step.order))
          );
        stepCandidates.set(step.order, candidates);
      }

      const singleSteps = [...stepCandidates.entries()]
        .filter(([, candidates]) => candidates.length === 1)
        .sort(([left], [right]) => left - right);
      const safeSingleStep = singleSteps.find(([stepOrder, candidates]) =>
        intervalRemainsSatisfiable({
          interval,
          steps: args.steps,
          candidatesForStep: args.candidatesForStep,
          discriminatingLiterals: args.discriminatingLiterals,
          claimsByStep: args.claimsByStep,
          claim: { stepOrder, actionIndex: candidates[0]!.index, layer: 4 },
        })
      );
      if (safeSingleStep) {
        const [stepOrder, candidates] = safeSingleStep;
        changed = args.tryClaim(stepOrder, candidates[0]!.index, 4) || changed;
        continue;
      }

      const candidateActionCount = new Set(
        [...stepCandidates.values()].flatMap((candidates) =>
          candidates.map((candidate) => candidate.index)
        )
      ).size;
      if (candidateActionCount <= openSteps.length) {
        const actionToSteps = new Map<number, number[]>();
        for (const [stepOrder, candidates] of stepCandidates) {
          for (const candidate of candidates) {
            const steps = actionToSteps.get(candidate.index) ?? [];
            steps.push(stepOrder);
            actionToSteps.set(candidate.index, steps);
          }
        }
        const singleActions = [...actionToSteps.entries()]
          .filter(([, steps]) => steps.length === 1)
          .sort(([left], [right]) => left - right);
        const safeSingleAction = singleActions.find(([actionIndex, stepOrders]) =>
          intervalRemainsSatisfiable({
            interval,
            steps: args.steps,
            candidatesForStep: args.candidatesForStep,
            discriminatingLiterals: args.discriminatingLiterals,
            claimsByStep: args.claimsByStep,
            claim: { stepOrder: stepOrders[0]!, actionIndex, layer: 4 },
          })
        );
        if (safeSingleAction) {
          const [actionIndex, stepOrders] = safeSingleAction;
          changed = args.tryClaim(stepOrders[0]!, actionIndex, 4) || changed;
        }
      }
    }
  }
}

export function actionIntervalForSteps(
  firstStepOrder: number,
  lastStepOrder: number,
  claimsByStep: ReadonlyMap<number, Pick<LinkClaim, 'stepOrder' | 'actionIndex'>>
): { afterAction: number; beforeAction: number } {
  let afterAction = -Infinity;
  let beforeAction = Infinity;
  for (const claim of claimsByStep.values()) {
    if (claim.stepOrder < firstStepOrder && claim.actionIndex > afterAction) {
      afterAction = claim.actionIndex;
    }
    if (claim.stepOrder > lastStepOrder && claim.actionIndex < beforeAction) {
      beforeAction = claim.actionIndex;
    }
  }
  return { afterAction, beforeAction };
}

function intervalRemainsSatisfiable(args: {
  interval: StepInterval;
  steps: PlaybookStep[];
  candidatesForStep: (step: PlaybookStep) => CandidateAction[];
  discriminatingLiterals: Map<number, LiteralSignal>;
  claimsByStep: ReadonlyMap<number, LinkClaim>;
  claim: LinkClaim;
}): boolean {
  const simulatedClaims = new Map(args.claimsByStep);
  simulatedClaims.set(args.claim.stepOrder, args.claim);
  for (const step of args.steps) {
    if (
      step.order === args.claim.stepOrder ||
      simulatedClaims.has(step.order) ||
      step.order <= args.interval.afterStep ||
      step.order >= args.interval.beforeStep
    ) {
      continue;
    }

    const dynamicInterval = actionIntervalForSteps(step.order, step.order, simulatedClaims);
    const hasCandidate = args
      .candidatesForStep(step)
      .some(
        (candidate) =>
          !isActionClaimed(simulatedClaims, candidate.index) &&
          candidate.index > args.interval.afterAction &&
          candidate.index < args.interval.beforeAction &&
          candidate.index > dynamicInterval.afterAction &&
          candidate.index < dynamicInterval.beforeAction &&
          stepMatchesCandidate(step, candidate, args.discriminatingLiterals.get(step.order))
      );
    if (!hasCandidate) return false;
  }
  return true;
}

function isActionClaimed(
  claimsByStep: ReadonlyMap<number, Pick<LinkClaim, 'actionIndex'>>,
  actionIndex: number
): boolean {
  return [...claimsByStep.values()].some((claim) => claim.actionIndex === actionIndex);
}
